import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { SocialIcon } from "react-social-icons";

export default function NavBar() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Sora:wght@700;800&display=swap');

        .nav-root {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          transition: background 0.3s, box-shadow 0.3s, backdrop-filter 0.3s;
          font-family: 'Inter', system-ui, sans-serif;
        }

        .nav-root.scrolled {
          background: rgba(8, 14, 28, 0.92);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow: 0 1px 0 rgba(99,102,241,0.15), 0 8px 32px rgba(0,0,0,0.4);
        }

        .nav-root.top {
          background: rgba(8, 14, 28, 0.4);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        .nav-inner {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 2rem;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        /* Logo */
        .nav-logo {
          font-family: 'Sora', system-ui, sans-serif;
          font-size: 1.4rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          text-decoration: none;
          background: linear-gradient(90deg, #818cf8, #38bdf8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          transition: opacity 0.2s;
        }

        .nav-logo:hover { opacity: 0.8; }

        /* Links */
        .nav-links {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .nav-link {
          font-size: 0.875rem;
          font-weight: 500;
          color: #64748b;
          text-decoration: none;
          padding: 0.45rem 0.85rem;
          border-radius: 8px;
          transition: color 0.2s, background 0.2s;
          letter-spacing: 0.01em;
        }

        .nav-link:hover {
          color: #e2e8f0;
          background: rgba(99,102,241,0.08);
        }

        .nav-link.active {
          color: #a5b4fc;
          background: rgba(99,102,241,0.12);
        }

        /* Social icons */
        .nav-social {
          display: flex;
          align-items: center;
          gap: 0.6rem;
        }

        .nav-social-icon {
          opacity: 0.6;
          transition: opacity 0.2s, transform 0.2s;
        }

        .nav-social-icon:hover {
          opacity: 1;
          transform: translateY(-1px);
        }

        /* Divider */
        .nav-divider {
          width: 1px;
          height: 20px;
          background: rgba(148,163,184,0.15);
          margin: 0 0.75rem;
        }

        /* Mobile hamburger */
        .nav-hamburger {
          display: none;
          flex-direction: column;
          gap: 5px;
          cursor: pointer;
          padding: 4px;
          background: none;
          border: none;
        }

        .nav-hamburger span {
          display: block;
          width: 22px;
          height: 2px;
          background: #94a3b8;
          border-radius: 2px;
          transition: all 0.25s;
        }

        /* Mobile menu */
        .nav-mobile {
          display: none;
          flex-direction: column;
          gap: 0.25rem;
          padding: 0.75rem 1.5rem 1.25rem;
          border-top: 1px solid rgba(99,102,241,0.1);
        }

        .nav-mobile .nav-link {
          font-size: 0.95rem;
          padding: 0.6rem 0.75rem;
        }

        .nav-mobile .nav-social {
          padding-top: 0.75rem;
          border-top: 1px solid rgba(99,102,241,0.08);
          margin-top: 0.25rem;
        }

        @media (max-width: 720px) {
          .nav-links, .nav-divider, .nav-social { display: none; }
          .nav-hamburger { display: flex; }
          .nav-mobile.open { display: flex; }
        }
      `}</style>

            <header className={`nav-root ${scrolled ? "scrolled" : "top"}`}>
                <div className="nav-inner">
                    {/* Logo */}
                    <NavLink to="/" className="nav-logo">
                        Rashi.
                    </NavLink>

                    {/* Desktop links */}
                    <nav className="nav-links">
                        <NavLink
                            to="/"
                            exact
                            className="nav-link"
                            activeClassName="active"
                        >
                            Home
                        </NavLink>
                        <NavLink
                            to="/about"
                            className="nav-link"
                            activeClassName="active"
                        >
                            About
                        </NavLink>
                        <NavLink
                            to="/job"
                            className="nav-link"
                            activeClassName="active"
                        >
                            Experience
                        </NavLink>
                        <NavLink
                            to="/project"
                            className="nav-link"
                            activeClassName="active"
                        >
                            Projects
                        </NavLink>
                    </nav>

                    {/* Social + divider */}
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <div className="nav-divider" />
                        <div className="nav-social">
                            <SocialIcon
                                url="https://linkedin.com/in/rashi~agrawal"
                                target="_blank"
                                fgColor="#94a3b8"
                                bgColor="transparent"
                                className="nav-social-icon"
                                style={{ height: 32, width: 32 }}
                            />
                            <SocialIcon
                                url="https://github.com/rashi-agrawal29"
                                target="_blank"
                                fgColor="#94a3b8"
                                bgColor="transparent"
                                className="nav-social-icon"
                                style={{ height: 32, width: 32 }}
                            />
                        </div>
                    </div>

                    {/* Hamburger */}
                    <button
                        className="nav-hamburger"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span style={menuOpen ? { transform: "translateY(7px) rotate(45deg)" } : {}} />
                        <span style={menuOpen ? { opacity: 0 } : {}} />
                        <span style={menuOpen ? { transform: "translateY(-7px) rotate(-45deg)" } : {}} />
                    </button>
                </div>

                {/* Mobile dropdown */}
                <div className={`nav-mobile ${menuOpen ? "open" : ""}`}>
                    <NavLink to="/" exact className="nav-link" activeClassName="active" onClick={() => setMenuOpen(false)}>Home</NavLink>
                    <NavLink to="/about" className="nav-link" activeClassName="active" onClick={() => setMenuOpen(false)}>About</NavLink>
                    <NavLink to="/job" className="nav-link" activeClassName="active" onClick={() => setMenuOpen(false)}>Experience</NavLink>
                    <NavLink to="/project" className="nav-link" activeClassName="active" onClick={() => setMenuOpen(false)}>Projects</NavLink>
                    <div className="nav-social">
                        <SocialIcon url="https://linkedin.com/in/rashi~agrawal" target="_blank" fgColor="#94a3b8" bgColor="transparent" style={{ height: 30, width: 30 }} />
                        <SocialIcon url="https://github.com/rashi-agrawal29" target="_blank" fgColor="#94a3b8" bgColor="transparent" style={{ height: 30, width: 30 }} />
                    </div>
                </div>
            </header>

            {/* Spacer so content doesn't hide under fixed nav */}
            <div style={{ height: 64 }} />
        </>
    );
}